import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { mediatorsAPI } from '../services/api';
import {
  Building2, LogOut, Copy, Link2, Users, TrendingUp, Network as NetworkIcon,
  Plus, Trash2, Loader2, UserCheck, Download, Megaphone, FileDown, MessageSquare,
  Mail, Linkedin, Eye, MousePointer2, Target, BarChart3,
  Facebook, MessageCircle, Send, Twitter, Share2
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCHF = (v) => `CHF ${(v || 0).toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

export const MediatorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [network, setNetwork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview | investors | network
  const [marketing, setMarketing] = useState(null);
  const [visitStats, setVisitStats] = useState(null);

  // Sub-mediator dialog
  const [isSubOpen, setIsSubOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subForm, setSubForm] = useState({
    firstName: '', lastName: '', email: '', password: '', commissionRate: 15
  });

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'mediator') {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const [ov, inv, net] = await Promise.all([
        mediatorsAPI.myOverview(),
        mediatorsAPI.myInvestors(),
        mediatorsAPI.myNetwork()
      ]);
      if (ov?.success) setOverview(ov.overview);
      if (inv?.success) setInvestors(inv.investors || []);
      if (net?.success) setNetwork(net.network || []);
      // Marketing kit (best effort)
      try {
        const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const r = await fetch(`${API_BASE}/api/mediators/me/marketing-kit/templates`, { credentials: 'include' });
        const data = await r.json();
        if (r.ok && data?.success) setMarketing(data);
      } catch { /* ignore */ }
      // Visit stats (best effort)
      try {
        const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const r = await fetch(`${API_BASE}/api/mediators/me/visit-stats`, { credentials: 'include' });
        const data = await r.json();
        if (r.ok && data?.success) setVisitStats(data.stats);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/vermittler/login');
  };

  const referralCode = user?.referralCode || overview?.me?.referralCode;
  const myRate = user?.commissionRate ?? overview?.me?.commissionRate ?? 0;
  const referralLink = typeof window !== 'undefined' && referralCode
    ? `${window.location.origin}/?ref=${referralCode}`
    : '';

  const copyLink = () => {
    navigator.clipboard?.writeText(referralLink);
    toast.success('Link kopiert');
  };

  const copyCode = () => {
    if (!referralCode) return;
    navigator.clipboard?.writeText(referralCode);
    toast.success('Code kopiert');
  };

  const handleCreateSub = async (e) => {
    e.preventDefault();
    if (Number(subForm.commissionRate) > Number(myRate)) {
      toast.error('Provisionssatz darf Ihren eigenen nicht übersteigen');
      return;
    }
    try {
      setSubmitting(true);
      await mediatorsAPI.createSub({
        firstName: subForm.firstName,
        lastName: subForm.lastName,
        email: subForm.email,
        password: subForm.password,
        commissionRate: Number(subForm.commissionRate)
      });
      toast.success('Untervermittler eingeladen');
      setIsSubOpen(false);
      setSubForm({ firstName: '', lastName: '', email: '', password: '', commissionRate: 15 });
      load();
    } catch (err) {
      toast.error(err.message || 'Konnte nicht anlegen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSub = async (m) => {
    if (!window.confirm(`"${m.firstName} ${m.lastName}" entfernen?`)) return;
    try {
      await mediatorsAPI.removeSub(m.id);
      toast.success('Untervermittler entfernt');
      load();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
  };

  if (loading || !overview) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
        Lädt...
      </div>
    );
  }

  const totalProvision = (overview.direct?.provision || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      {/* Header */}
      <header className="bg-[#1a1a1a] border-b border-[#C4A55A]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Building2 className="h-8 w-8 text-[#C4A55A]" />
              <div>
                <h1 className="text-2xl font-bold text-white">Vermittler-Portal</h1>
                <p className="text-sm text-gray-400">Löwenherz Invest AG</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Hallo, {user?.firstName}</span>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
                data-testid="mediator-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Provision model explanation */}
        <div
          data-testid="mediator-info-box"
          className="mb-6 bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-5"
        >
          <div className="flex items-start gap-3">
            <NetworkIcon className="h-5 w-5 text-[#C4A55A] mt-1 shrink-0" />
            <div>
              <h3 className="text-white font-semibold mb-1">So funktioniert Ihr Vermittler-Portal</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Teilen Sie Ihren persönlichen Referral-Link mit potenziellen Investoren. Jeder Investor,
                der sich darüber registriert und Aktien zeichnet, wird automatisch Ihrem Konto zugeordnet.
                Sie erhalten <strong className="text-[#C4A55A]">{myRate}%</strong> Provision auf das eigene
                Investitions­volumen, sobald die Zeichnung von uns bestätigt und die Zahlung eingegangen ist.
                Untervermittler können einen kleineren Satz erhalten – die Differenz verbleibt bei Ihnen.
              </p>
            </div>
          </div>
        </div>

        {/* Referral Link banner */}
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#C4A55A]/15 via-[#1a1a1a] to-[#1a1a1a] border border-[#C4A55A]/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Ihr Referral-Link</p>
              <p className="text-white font-mono text-sm md:text-base break-all">
                {referralLink}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={copyCode}
                variant="outline"
                className="border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
                data-testid="mediator-copy-code-btn"
              >
                <Link2 className="h-4 w-4 mr-2" />
                {referralCode}
              </Button>
              <Button
                onClick={copyLink}
                className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                data-testid="mediator-copy-link-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Link kopieren
              </Button>
            </div>
          </div>
        </div>

        {/* Marketing Kit */}
        <MarketingKit marketing={marketing} />

        {/* Visit Stats */}
        <VisitStats stats={visitStats} />

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Tile icon={Users} label="Eigene Investoren" value={overview.direct.investors} />
          <Tile icon={TrendingUp} label="Eigenes Volumen" value={fmtCHF(overview.direct.investmentVolume)} />
          <Tile icon={NetworkIcon} label="Untervermittler" value={overview.network.subMediators} />
          <Tile
            icon={UserCheck}
            label={`Provision (${myRate}%)`}
            value={fmtCHF(totalProvision)}
            accent
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-[#C4A55A]/15 overflow-x-auto">
          {[
            { id: 'overview', label: 'Übersicht' },
            { id: 'investors', label: `Meine Investoren (${investors.length})` },
            { id: 'network', label: `Mein Netzwerk (${network.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`mediator-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-[#C4A55A] text-[#C4A55A]'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto">
            <Dialog open={isSubOpen} onOpenChange={setIsSubOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                  data-testid="mediator-add-sub-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Untervermittler einladen
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-white">Untervermittler einladen</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Max. Provisionssatz: {myRate}%
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSub} className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Vorname</Label>
                      <Input
                        value={subForm.firstName}
                        onChange={(e) => setSubForm({ ...subForm, firstName: e.target.value })}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                        required
                        data-testid="mediator-sub-firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Nachname</Label>
                      <Input
                        value={subForm.lastName}
                        onChange={(e) => setSubForm({ ...subForm, lastName: e.target.value })}
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                        required
                        data-testid="mediator-sub-lastname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">E-Mail</Label>
                    <Input
                      type="email"
                      value={subForm.email}
                      onChange={(e) => setSubForm({ ...subForm, email: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="mediator-sub-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Passwort (min. 8 Zeichen)</Label>
                    <Input
                      type="password"
                      value={subForm.password}
                      onChange={(e) => setSubForm({ ...subForm, password: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      minLength={8}
                      data-testid="mediator-sub-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Provisionssatz (%) – max. {myRate}%</Label>
                    <Input
                      type="number"
                      min={0}
                      max={myRate}
                      step="0.1"
                      value={subForm.commissionRate}
                      onChange={(e) => setSubForm({ ...subForm, commissionRate: e.target.value })}
                      className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                      required
                      data-testid="mediator-sub-rate-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                    data-testid="mediator-sub-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Erstelle...</>
                    ) : 'Einladung erstellen'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20" data-testid="mediator-overview">
            <CardHeader>
              <CardTitle className="text-white">Netzwerk-Übersicht</CardTitle>
              <CardDescription className="text-gray-400">
                Direkte Aktivität (von Ihnen geworbene Investoren) + Aktivität Ihres Netzwerks.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-black/30 border border-[#C4A55A]/15">
                  <p className="text-xs text-gray-400 uppercase mb-2">Direkt</p>
                  <p className="text-white text-2xl font-bold">{overview.direct.investors} Investoren</p>
                  <p className="text-gray-300 text-sm mt-1">
                    Volumen: <span className="text-[#C4A55A] font-semibold">{fmtCHF(overview.direct.investmentVolume)}</span>
                  </p>
                  <p className="text-gray-300 text-sm">
                    Provision: <span className="text-[#C4A55A] font-semibold">{fmtCHF(overview.direct.provision)}</span>
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-black/30 border border-[#C4A55A]/15">
                  <p className="text-xs text-gray-400 uppercase mb-2">Netzwerk (Untervermittler)</p>
                  <p className="text-white text-2xl font-bold">{overview.network.subMediators} Vermittler</p>
                  <p className="text-gray-300 text-sm mt-1">
                    Investoren: <span className="text-white">{overview.network.networkInvestors}</span>
                  </p>
                  <p className="text-gray-300 text-sm">
                    Volumen: <span className="text-[#C4A55A] font-semibold">{fmtCHF(overview.network.networkInvestmentVolume)}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'investors' && (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20" data-testid="mediator-investors-table">
            <CardHeader>
              <CardTitle className="text-white">Meine Investoren</CardTitle>
              <CardDescription className="text-gray-400">
                Direkt über Ihren Referral-Code geworbene Investoren · Provisionssatz: {myRate}%
              </CardDescription>
            </CardHeader>
            <CardContent>
              {investors.length === 0 ? (
                <p className="text-gray-500 py-12 text-center" data-testid="mediator-investors-empty">
                  Sie haben noch keine Investoren geworben. Teilen Sie Ihren Link.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-[#C4A55A]/15">
                        <th className="py-2 font-medium">Name</th>
                        <th className="py-2 font-medium">E-Mail</th>
                        <th className="py-2 font-medium text-right">Investment</th>
                        <th className="py-2 font-medium text-right">Provision</th>
                        <th className="py-2 font-medium text-right">Seit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investors.map((i) => (
                        <tr key={i.id} className="border-b border-[#C4A55A]/10 hover:bg-[#C4A55A]/5">
                          <td className="py-2.5 text-white">{i.firstName} {i.lastName}</td>
                          <td className="py-2.5 text-gray-400">{i.email}</td>
                          <td className="py-2.5 text-right text-white">{fmtCHF(i.investment)}</td>
                          <td className="py-2.5 text-right text-[#C4A55A] font-semibold">{fmtCHF(i.provision)}</td>
                          <td className="py-2.5 text-right text-gray-500">{fmtDate(i.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'network' && (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20" data-testid="mediator-network-list">
            <CardHeader>
              <CardTitle className="text-white">Mein Netzwerk</CardTitle>
              <CardDescription className="text-gray-400">
                Alle Untervermittler unter Ihnen (rekursiv, alle Ebenen)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {network.length === 0 ? (
                <p className="text-gray-500 py-12 text-center" data-testid="mediator-network-empty">
                  Sie haben noch keine Untervermittler. Klicken Sie auf „Untervermittler einladen".
                </p>
              ) : (
                <div className="space-y-3">
                  {network.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`mediator-network-row-${m.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-black/30 border border-[#C4A55A]/10"
                    >
                      <div className="min-w-0">
                        <p className="text-white font-medium">{m.firstName} {m.lastName}</p>
                        <p className="text-xs text-gray-500 truncate">{m.email} · Code: <span className="text-[#C4A55A] font-mono">{m.referralCode}</span></p>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="text-gray-300">
                          {m.directInvestorCount} Inv. · <span className="text-[#C4A55A]">{fmtCHF(m.directInvestmentVolume)}</span>
                        </span>
                        <span className="px-2 py-0.5 text-xs rounded bg-[#C4A55A]/15 text-[#C4A55A]">
                          {m.commissionRate}%
                        </span>
                        {/* Only allow delete if it's a direct child */}
                        {m.parentMediatorId === user.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                            onClick={() => handleDeleteSub(m)}
                            data-testid={`mediator-network-delete-${m.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

const TEMPLATE_ICONS = {
  whatsapp: MessageSquare,
  email: Mail,
  linkedin: Linkedin,
  sms: MessageSquare,
};

const MarketingKit = ({ marketing }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  if (!marketing) return null;

  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const flyerUrl = `${apiBase}/api/mediators/me/marketing-kit/flyer.pdf`;
  const referralLink = marketing.referralLink || '';

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('In Zwischenablage kopiert');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  // Build channel-specific share-intent URLs for each template
  const enc = encodeURIComponent;
  const buildShareUrls = (key, tpl) => {
    const text = tpl.text || '';
    const subject = tpl.subject || 'Investitionsmöglichkeit – Löwenherz Invest AG';
    const primary = (() => {
      switch (key) {
        case 'whatsapp': return { label: 'In WhatsApp öffnen', icon: MessageCircle, color: '#25D366',
          href: `https://api.whatsapp.com/send?text=${enc(text)}` };
        case 'email':    return { label: 'In Mail-App öffnen', icon: Mail, color: '#C4A55A',
          href: `mailto:?subject=${enc(subject)}&body=${enc(text)}` };
        case 'linkedin': return { label: 'Auf LinkedIn teilen', icon: Linkedin, color: '#0A66C2',
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(referralLink)}` };
        case 'sms':      return { label: 'SMS-App öffnen', icon: MessageSquare, color: '#34B7F1',
          href: `sms:?body=${enc(text)}` };
        default: return null;
      }
    })();

    const secondary = [
      { key: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2',
        href: `https://www.facebook.com/sharer/sharer.php?u=${enc(referralLink)}&quote=${enc(text)}` },
      { key: 'twitter', label: 'X / Twitter', icon: Twitter, color: '#000000',
        href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(referralLink)}` },
      { key: 'telegram', label: 'Telegram', icon: Send, color: '#0088cc',
        href: `https://t.me/share/url?url=${enc(referralLink)}&text=${enc(text)}` },
    ];

    return { primary, secondary };
  };

  const tpls = marketing.templates || {};

  return (
    <section
      data-testid="mediator-marketing-kit"
      className="mb-6 bg-gradient-to-br from-[#1a1a1a] via-[#15110a] to-[#1a1a1a] border border-[#C4A55A]/30 rounded-2xl p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-[#C4A55A]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Marketing-Kit</h3>
            <p className="text-xs text-gray-400">Vorgefertigte Inhalte mit Ihrem Referral-Link – mit 1 Klick versenden</p>
          </div>
        </div>
        <a
          href={flyerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mediator-marketing-flyer-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold text-sm"
        >
          <FileDown className="h-4 w-4" />
          Personalisierten PDF-Flyer herunterladen
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(tpls).map(([key, tpl]) => {
          const Icon = TEMPLATE_ICONS[key] || MessageSquare;
          const copied = copiedKey === key;
          const { primary, secondary } = buildShareUrls(key, tpl);
          return (
            <div
              key={key}
              data-testid={`mediator-tpl-${key}`}
              className="bg-black/40 border border-[#C4A55A]/15 rounded-xl p-3 hover:border-[#C4A55A]/40 transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-[#C4A55A]" />
                  <span className="text-sm font-semibold text-white">{tpl.label}</span>
                </div>
                <button
                  onClick={() => copy(key, tpl.text)}
                  data-testid={`mediator-tpl-copy-${key}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md transition-colors ${
                    copied ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#C4A55A]/15 hover:bg-[#C4A55A] text-[#C4A55A] hover:text-black'
                  }`}
                >
                  <Copy className="h-3 w-3" />
                  {copied ? 'Kopiert!' : 'Kopieren'}
                </button>
              </div>
              {tpl.subject && (
                <div className="text-xs text-gray-400 mb-1">
                  <span className="uppercase tracking-wider mr-2">Betreff:</span>
                  <span className="text-gray-200">{tpl.subject}</span>
                </div>
              )}
              <pre className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed bg-black/40 border border-white/5 rounded-md p-3 max-h-48 overflow-auto font-sans">
                {tpl.text}
              </pre>

              {/* 1-Klick Versand-Buttons */}
              <div className="mt-3 pt-3 border-t border-white/5">
                {primary && (
                  <a
                    href={primary.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mediator-send-${key}`}
                    className="inline-flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-white text-sm font-semibold transition-transform hover:scale-[1.02] mb-2"
                    style={{ backgroundColor: primary.color, color: key === 'email' ? '#000' : '#fff' }}
                  >
                    <primary.icon className="h-4 w-4" />
                    {primary.label}
                  </a>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 mr-1">
                    <Share2 className="h-3 w-3" /> Auch teilen:
                  </span>
                  {secondary.map((s) => (
                    <a
                      key={s.key}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.label}
                      data-testid={`mediator-share-${key}-${s.key}`}
                      className="h-7 w-7 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110"
                      style={{ backgroundColor: s.color }}
                    >
                      <s.icon className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const VisitStats = ({ stats }) => {
  if (!stats) return null;
  const { totalVisits = 0, weekVisits = 0, investors = 0, conversionRate = 0, series = [] } = stats;

  // mini bar chart
  const max = series.reduce((m, d) => Math.max(m, d.count || 0), 0) || 1;

  return (
    <section
      data-testid="mediator-visit-stats"
      className="mb-6 bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-6"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center">
          <BarChart3 className="h-5 w-5 text-[#C4A55A]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Klick-Statistiken</h3>
          <p className="text-xs text-gray-400">Wie oft wurde Ihr persönlicher Referral-Link aufgerufen?</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatTile icon={MousePointer2} label="Klicks gesamt" value={totalVisits} testId="stat-total-visits" />
        <StatTile icon={Eye} label="Letzte 7 Tage" value={weekVisits} testId="stat-week-visits" />
        <StatTile icon={UserCheck} label="Registriert" value={investors} testId="stat-investors" />
        <StatTile icon={Target} label="Conversion" value={`${conversionRate}%`} testId="stat-conversion" />
      </div>

      {/* Mini bar chart */}
      <div className="bg-black/40 border border-white/5 rounded-xl p-4">
        <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">Verlauf · letzte 14 Tage</div>
        {series.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">Noch keine Klicks im letzten 14-Tage-Zeitraum.</p>
        ) : (
          <div className="flex items-end gap-1.5 h-24">
            {series.map((d) => {
              const h = (d.count / max) * 100;
              return (
                <div
                  key={d.date}
                  className="group relative flex-1 flex flex-col justify-end"
                  title={`${d.date}: ${d.count}`}
                >
                  <div
                    className="w-full bg-gradient-to-t from-[#C4A55A] to-[#D4B56A] rounded-sm transition-all hover:opacity-80"
                    style={{ height: `${Math.max(h, 4)}%` }}
                  />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded bg-black/90 border border-[#C4A55A]/30 text-[10px] text-[#C4A55A] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {d.count}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-3">
        Hinweis: Pro Browser-Session wird Ihr Link einmal gezählt, um die Zahlen aussagekräftig zu halten.
      </p>
    </section>
  );
};

const StatTile = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="bg-black/40 border border-[#C4A55A]/15 rounded-xl p-3 hover:border-[#C4A55A]/40 transition-colors"
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-3.5 w-3.5 text-[#C4A55A]" />
      <span className="text-[10px] uppercase tracking-wider text-gray-400">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
  </div>
);

const Tile = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl p-5 border ${
    accent ? 'bg-gradient-to-br from-[#C4A55A]/15 to-[#1a1a1a] border-[#C4A55A]/40' : 'bg-[#1a1a1a] border-[#C4A55A]/20'
  }`}>
    <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide mb-2">
      <Icon className="h-3.5 w-3.5 text-[#C4A55A]" />
      {label}
    </div>
    <div className="text-2xl md:text-3xl font-bold text-white">{value}</div>
  </div>
);
